// src/pages/Customers.js
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  FaUsers,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaShoppingBag,
  FaRupeeSign,
  FaCalendarAlt,
  FaTimes,
  FaBox,
  FaSearch
} from 'react-icons/fa';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/all`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setCustomers(data);
      setFilteredCustomers(data);
    } catch (err) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCustomers(customers);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredCustomers(
        customers.filter(
          (customer) =>
            customer.name?.toLowerCase().includes(query) ||
            customer.email?.toLowerCase().includes(query) ||
            customer.phone?.includes(query)
        )
      );
    }
  }, [searchQuery, customers]);

  const openCustomerDetails = async (customer) => {
    setSelectedCustomer(customer);
    setShowModal(true);
    setModalLoading(true);

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/customers/${customer._id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load customer details');
      const data = await res.json();
      setCustomerOrders(data.orders || []);
    } catch (err) {
      toast.error('Failed to load customer orders');
      setCustomerOrders([]);
    } finally {
      setModalLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending': return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
      case 'confirmed': return { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' };
      case 'processing': return { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' };
      case 'shipped': return { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' };
      case 'delivered': return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
      case 'cancelled': return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
      default: return { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' };
    }
  };

  // Calculate stats
  const totalCustomers = customers.length;
  const customersWithOrders = customers.filter(c => c.orderCount > 0).length;
  const totalRevenue = customers.reduce((sum, c) => sum + (c.totalSpent || 0), 0);
  const avgOrderValue = customersWithOrders > 0
    ? totalRevenue / customers.reduce((sum, c) => sum + c.orderCount, 0)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading customers...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-2xl font-bold text-slate-800">Customer Management</h1>
          <p className="text-slate-500 mt-1">{totalCustomers} registered customers</p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-blue-50 border border-blue-200 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <FaUsers className="text-blue-600 text-sm" />
              <span className="text-xs font-medium text-blue-700">Total Customers</span>
            </div>
            <p className="text-2xl font-bold text-blue-700">{totalCustomers}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="bg-emerald-50 border border-emerald-200 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <FaShoppingBag className="text-emerald-600 text-sm" />
              <span className="text-xs font-medium text-emerald-700">With Orders</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">{customersWithOrders}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-purple-50 border border-purple-200 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <FaRupeeSign className="text-purple-600 text-sm" />
              <span className="text-xs font-medium text-purple-700">Total Revenue</span>
            </div>
            <p className="text-2xl font-bold text-purple-700">₹{totalRevenue.toFixed(2)}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <FaBox className="text-amber-600 text-sm" />
              <span className="text-xs font-medium text-amber-700">Avg Order Value</span>
            </div>
            <p className="text-2xl font-bold text-amber-700">₹{avgOrderValue.toFixed(2)}</p>
          </motion.div>
        </div>

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
          </div>
        </motion.div>

        {/* Customers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCustomers.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-full text-center py-16 bg-white rounded-xl shadow-card border border-slate-100"
            >
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FaUsers className="text-2xl text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-2">No customers found</h3>
              <p className="text-slate-500">
                {searchQuery ? 'Try a different search term' : 'Customers will appear here once they sign up'}
              </p>
            </motion.div>
          ) : (
            filteredCustomers.map((customer, index) => (
              <motion.div
                key={customer._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ y: -2 }}
                onClick={() => openCustomerDetails(customer)}
                className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden hover:shadow-soft transition-all duration-300 cursor-pointer"
              >
                <div className="p-5">
                  {/* Customer Header */}
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {customer.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800 truncate">{customer.name}</h3>
                      <p className="text-sm text-slate-500 truncate">{customer.email}</p>
                    </div>
                  </div>

                  {/* Customer Details */}
                  <div className="space-y-2 text-sm">
                    {customer.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <FaPhone className="text-slate-400 text-xs" />
                        <span>{customer.phone}</span>
                      </div>
                    )}
                    {customer.address?.city && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <FaMapMarkerAlt className="text-slate-400 text-xs" />
                        <span>{customer.address.city}, {customer.address.state}</span>
                      </div>
                    )}
                  </div>

                  {/* Stats Row */}
                  <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold text-primary-600">{customer.orderCount || 0}</p>
                      <p className="text-xs text-slate-500">Orders</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-emerald-600">₹{(customer.totalSpent || 0).toFixed(0)}</p>
                      <p className="text-xs text-slate-500">Spent</p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-700">
                        {customer.lastOrderDate
                          ? new Date(customer.lastOrderDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })
                          : '-'
                        }
                      </p>
                      <p className="text-xs text-slate-500">Last Order</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Customer Details Modal */}
      <AnimatePresence>
        {showModal && selectedCustomer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6 rounded-t-2xl">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center text-white font-bold text-xl">
                      {selectedCustomer.name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white">{selectedCustomer.name}</h2>
                      <p className="text-primary-100 text-sm">Customer since {new Date(selectedCustomer.createdAt).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                  >
                    <FaTimes className="text-white" />
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {/* Customer Info */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <FaUser className="text-primary-600" />
                      Contact Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <FaEnvelope className="text-slate-400" />
                        <span className="text-slate-600">{selectedCustomer.email}</span>
                      </div>
                      {selectedCustomer.phone && (
                        <div className="flex items-center gap-2">
                          <FaPhone className="text-slate-400" />
                          <span className="text-slate-600">{selectedCustomer.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4">
                    <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                      <FaMapMarkerAlt className="text-primary-600" />
                      Address
                    </h3>
                    {selectedCustomer.address?.street ? (
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {selectedCustomer.address.street}<br />
                        {selectedCustomer.address.city}, {selectedCustomer.address.state}<br />
                        {selectedCustomer.address.pincode}, {selectedCustomer.address.country || 'India'}
                      </p>
                    ) : (
                      <p className="text-sm text-slate-400 italic">No address provided</p>
                    )}
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-primary-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-primary-600">{selectedCustomer.orderCount || 0}</p>
                    <p className="text-sm text-primary-700">Total Orders</p>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-600">₹{(selectedCustomer.totalSpent || 0).toFixed(2)}</p>
                    <p className="text-sm text-emerald-700">Total Spent</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">
                      {selectedCustomer.orderCount > 0
                        ? `₹${(selectedCustomer.totalSpent / selectedCustomer.orderCount).toFixed(0)}`
                        : '-'
                      }
                    </p>
                    <p className="text-sm text-amber-700">Avg Order</p>
                  </div>
                </div>

                {/* Orders List */}
                <div>
                  <h3 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
                    <FaShoppingBag className="text-primary-600" />
                    Order History ({customerOrders.length})
                  </h3>

                  {modalLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
                    </div>
                  ) : customerOrders.length === 0 ? (
                    <div className="text-center py-8 bg-slate-50 rounded-xl">
                      <FaShoppingBag className="text-3xl text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500">No orders yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {customerOrders.map((order) => {
                        const statusConfig = getStatusConfig(order.status);
                        return (
                          <div
                            key={order._id}
                            className="bg-slate-50 rounded-xl p-4 border border-slate-100"
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className="font-semibold text-slate-800">
                                  #{order._id.slice(-8).toUpperCase()}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.text} ${statusConfig.border} border`}>
                                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                                </span>
                              </div>
                              <span className="font-bold text-slate-800">₹{order.totalPrice.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2 text-slate-500">
                                <FaCalendarAlt className="text-xs" />
                                {new Date(order.createdAt).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </div>
                              <span className="text-slate-500">
                                {order.products?.length || 0} item(s)
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Customers;
